import React from "react";
import { Box, Typography } from "@mui/material";
import {
  SectionHeader,
  SubSectionHeader,
  ColumnHeader,
  ItemRow,
  ItemIcon,
  ItemName,
} from "./styles";
import { toPlainText } from "../../../components/RichText";
import type { MSHatData, SimpleNamedItem } from "../../types";

// --- Helpers ---

const hasItems = (items?: SimpleNamedItem[]): boolean =>
  !!items && items.length > 0;

const NamedItemList = ({ items }: { items?: SimpleNamedItem[] }) => {
  if (!items || items.length === 0) return null;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      {items.map((item) => (
        <ItemRow key={item.id}>
          {item.icon?.url && (
            <ItemIcon>
              <img
                src={item.icon.url}
                alt=""
                width={24}
                height={24}
                style={{ objectFit: "contain" }}
              />
            </ItemIcon>
          )}
          <ItemName>{item.name}</ItemName>
        </ItemRow>
      ))}
    </Box>
  );
};

// Keys to Success carry a hat-specific rich text description per row,
// unlike the other simple MS lists.
const descriptionToPlainText = (description: unknown): string => {
  if (!description) return "";
  const asString =
    typeof description === "string" ? description : JSON.stringify(description);
  return toPlainText(asString);
};

const KeysToSuccessList = ({ items }: { items?: SimpleNamedItem[] }) => {
  if (!items || items.length === 0) return null;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {items.map((item) => {
        const desc = descriptionToPlainText(item.description);
        return (
          <Box
            key={item.id}
            sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}
          >
            <ItemRow>
              {item.icon?.url && (
                <ItemIcon>
                  <img
                    src={item.icon.url}
                    alt=""
                    width={24}
                    height={24}
                    style={{ objectFit: "contain" }}
                  />
                </ItemIcon>
              )}
              <ItemName>{item.name}</ItemName>
            </ItemRow>
            {desc && (
              <Typography
                sx={{
                  fontSize: 13,
                  lineHeight: 1.4,
                  color: "text.secondary",
                  pl: 4,
                }}
              >
                {desc}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

// --- Main component ---

export const EducationSection = ({ hat }: { hat: MSHatData }) => {
  const hasAnyEducation =
    hasItems(hat.msConnectedCourses) ||
    hasItems(hat.msKeysToSuccess) ||
    hasItems(hat.msPopularPaths) ||
    hasItems(hat.msOtherRoutes);

  if (!hasAnyEducation) return null;

  return (
    <>
      <SectionHeader>Education</SectionHeader>

      {(hasItems(hat.msConnectedCourses) || hasItems(hat.msKeysToSuccess)) && (
        <>
          <SubSectionHeader>High School Priorities</SubSectionHeader>
          <Box sx={{ display: "flex", gap: 3 }}>
            {hasItems(hat.msConnectedCourses) && (
              <Box sx={{ flex: 1 }}>
                <ColumnHeader sx={{ mb: 1 }}>Connected Courses</ColumnHeader>
                <NamedItemList items={hat.msConnectedCourses} />
              </Box>
            )}
            {hasItems(hat.msKeysToSuccess) && (
              <Box sx={{ flex: 1 }}>
                <ColumnHeader sx={{ mb: 1 }}>Keys to Success</ColumnHeader>
                <KeysToSuccessList items={hat.msKeysToSuccess} />
              </Box>
            )}
          </Box>
        </>
      )}

      {(hasItems(hat.msPopularPaths) || hasItems(hat.msOtherRoutes)) && (
        <>
          <SubSectionHeader>After High School</SubSectionHeader>
          <Box sx={{ display: "flex", gap: 3 }}>
            {hasItems(hat.msPopularPaths) && (
              <Box sx={{ flex: 1 }}>
                <ColumnHeader sx={{ mb: 1 }}>Popular Paths</ColumnHeader>
                <NamedItemList items={hat.msPopularPaths} />
              </Box>
            )}
            {hasItems(hat.msOtherRoutes) && (
              <Box sx={{ flex: 1 }}>
                <ColumnHeader sx={{ mb: 1 }}>Other Routes</ColumnHeader>
                <NamedItemList items={hat.msOtherRoutes} />
              </Box>
            )}
          </Box>
        </>
      )}
    </>
  );
};
